import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";
import Reveal from "@/components/Reveal";
import SEOHead from "@/components/SEOHead";
import { listProjectPhotos, resolvePhotoUrl } from "@/lib/api";
import { photoDims } from "@/lib/photos";
import { track } from "@/lib/analytics";

export default function Projects() {
    const [photos, setPhotos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        listProjectPhotos()
            .then(setPhotos)
            .catch(() => setPhotos([]))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div data-testid="projects-page">
            <SEOHead
                title="Harris Homes Projects | Custom Homes in Tahlequah, OK"
                description="Photos of custom homes, remodels and additions built by Harris Homes in Tahlequah and Cherokee County, OK. See the work before you call."
                canonical="https://harrishomesok.com/projects"
            />
            {/* Header */}
            <section className="pt-20 md:pt-32 pb-16 md:pb-20 px-6 md:px-10">
                <div className="max-w-[1480px] mx-auto">
                    <Reveal>
                        <div className="overline mb-6">Selected Work</div>
                        <h1 className="font-display text-5xl sm:text-7xl md:text-[7.5rem] leading-[0.95] tracking-tight max-w-5xl">
                            Custom homes we've built
                            <br />
                            <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}>
                                in Tahlequah.
                            </span>
                        </h1>
                        <p className="mt-10 max-w-2xl text-base md:text-lg leading-relaxed" style={{ color: "var(--hh-text-dim)" }}>
                            A look at finished homes, in-progress builds, and the details
                            we obsess over. Tap any image to view it larger. Curious about
                            how we work? Read more about our approach as a{" "}
                            <Link
                                to="/custom-home-builder-tahlequah-ok"
                                data-testid="projects-tahlequah-link"
                                className="underline underline-offset-4 decoration-1 transition-colors hover:text-[var(--hh-gold-light)]"
                                style={{ color: "var(--hh-gold)" }}
                            >
                                custom home builder in Tahlequah, Oklahoma
                            </Link>
                            .
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* Masonry-style grid */}
            <section className="px-6 md:px-10 pb-24">
                <div className="max-w-[1480px] mx-auto">
                    {loading ? (
                        <div className="py-20 text-center overline" data-testid="projects-loading">
                            Loading the gallery…
                        </div>
                    ) : photos.length === 0 ? (
                        <div className="py-20 text-center overline" data-testid="projects-empty">
                            No projects to show yet.
                        </div>
                    ) : (
                        <div className="columns-1 sm:columns-2 lg:columns-3 gap-3 md:gap-4 [column-fill:_balance]">
                            {photos.map((p, i) => (
                                <Reveal key={p.id} delay={Math.min(i * 0.03, 0.4)}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSelected(p);
                                            track.galleryOpen(p.id);
                                        }}
                                        data-testid={`gallery-thumb-${i}`}
                                        className="mb-3 md:mb-4 block w-full break-inside-avoid relative img-zoom group"
                                        style={{ background: "var(--hh-bg-2)" }}
                                    >
                                        <img
                                            src={resolvePhotoUrl(p.url)}
                                            alt={p.caption || `Custom home project ${i + 1} by Harris Homes in Tahlequah, OK`}
                                            {...(photoDims(p.url) ? { width: photoDims(p.url).w, height: photoDims(p.url).h } : {})}
                                            className="w-full h-auto object-cover"
                                            loading={i < 6 ? "eager" : "lazy"}
                                        />
                                        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-5"
                                            style={{ background: "linear-gradient(180deg, transparent 50%, rgba(10,9,8,0.7) 100%)" }}>
                                            <span
                                                className="text-[10px] tracking-[0.4em] uppercase"
                                                style={{ color: "var(--hh-gold-light)", fontFamily: "Marcellus, serif" }}
                                            >
                                                {p.caption || "View Larger"}
                                            </span>
                                        </div>
                                    </button>
                                </Reveal>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Lightbox */}
            {selected && (
                <div
                    data-testid="lightbox"
                    className="fixed inset-0 z-[80] flex items-center justify-center p-4 md:p-10"
                    style={{ background: "rgba(6,5,4,0.96)" }}
                    onClick={() => setSelected(null)}
                >
                    <button
                        type="button"
                        data-testid="lightbox-close"
                        onClick={() => setSelected(null)}
                        className="absolute top-6 right-6 text-[var(--hh-text)] hover:text-[var(--hh-gold)] transition-colors"
                        aria-label="Close"
                    >
                        <X size={32} strokeWidth={1.2} />
                    </button>
                    <img
                        src={resolvePhotoUrl(selected.url)}
                        alt={selected.caption || "Project full view"}
                        className="max-w-full max-h-full object-contain"
                        onClick={(e) => e.stopPropagation()}
                    />
                    {selected.caption && (
                        <div
                            className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.4em] uppercase"
                            style={{ color: "var(--hh-gold-light)", fontFamily: "Marcellus, serif" }}
                        >
                            {selected.caption}
                        </div>
                    )}
                </div>
            )}

            {/* CTA */}
            <section className="py-24 md:py-32 px-6 md:px-10 text-center" style={{ background: "var(--hh-bg-2)" }}>
                <Reveal>
                    <h2 className="font-display text-4xl md:text-6xl leading-tight tracking-tight max-w-3xl mx-auto">
                        Imagining your own?
                        <br />
                        <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}>
                            We'd love to hear about it.
                        </span>
                    </h2>
                    <div className="mt-10">
                        <Link to="/contact" data-testid="projects-cta" className="btn-gold">
                            Start Your Project <ArrowRight size={14} />
                        </Link>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
